import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as promptContextsApi from '../api/aiPromptContexts';

export interface AiPromptContext {
    aiPromptContextId: string;
    aiPromptId: string;
    facilityId: number;
    aiPromptContextText?: string;
    actvInd: boolean;
    insDttm: Date;
    updDttm: Date;
    audVer: number;
}

export const promptContextsKey = ['prompt-contexts'] as const;
export const promptContextsByFacilityKey = (facilityId: number) => ['prompt-contexts', facilityId] as const;
export const promptContextKey = (promptContextId: string) => ['prompt-context', promptContextId] as const;

export const usePromptContexts = (facilityId: AiPromptContext['facilityId']) => {
    return useQuery({
        queryKey: promptContextsByFacilityKey(facilityId),
        queryFn: () => promptContextsApi.getPromptContexts(facilityId),
        enabled: !!facilityId,
    });
};

export const usePromptContext = (promptContextId: string) => {
    return useQuery({
        queryKey: promptContextKey(promptContextId),
        queryFn: () => promptContextsApi.getPromptContextById(promptContextId),
        enabled: !!promptContextId,
    });
};

export const useCreatePromptContext = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (promptContextData: Partial<AiPromptContext>) =>
            promptContextsApi.createPromptContext(promptContextData),
        onSuccess: (_, variables) => {
            if (variables.facilityId) {
                queryClient.invalidateQueries({ queryKey: promptContextsByFacilityKey(variables.facilityId) });
            }
        },
    });
};

export const useUpdatePromptContext = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            promptContextId,
            promptContextData,
        }: {
            promptContextId: string;
            promptContextData: Partial<AiPromptContext>;
        }) => promptContextsApi.updatePromptContext(promptContextId, promptContextData),
        onSuccess: (_, { promptContextId, promptContextData }) => {
            if (promptContextData.facilityId) {
                queryClient.invalidateQueries({ queryKey: promptContextsByFacilityKey(promptContextData.facilityId) });
            }
            queryClient.invalidateQueries({ queryKey: promptContextKey(promptContextId) });
        },
    });
};

export const useActivatePromptContext = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (promptContextId: string) => promptContextsApi.activatePromptContext(promptContextId),
        onSuccess: (_, promptContextId) => {
            queryClient.invalidateQueries({ queryKey: ['prompt-contexts'] });
            queryClient.invalidateQueries({ queryKey: promptContextKey(promptContextId) });
        },
    });
};

export const useDeactivatePromptContext = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (promptContextId: string) => promptContextsApi.deactivatePromptContext(promptContextId),
        onSuccess: (_, promptContextId) => {
            queryClient.invalidateQueries({ queryKey: ['prompt-contexts'] });
            queryClient.invalidateQueries({ queryKey: promptContextKey(promptContextId) });
        },
    });
};
